import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faExchangeAlt,
    faStore,
    faSearch,
    faPlus,
    faCheckCircle,
    faTimes,
    faSave,
    faCircle,
    faExclamationTriangle,
    faSync,
    faTag,
    faBarcode,
    faTrashAlt,
    faArrowRight,
    faArrowLeft,
    faClock,
    faCheck,
    faBan,
    faInfoCircle,
    faHistory,
    faFileAlt,
    faBox,
    faClipboardList,
    faCopy,
    faFileExport
} from '@fortawesome/free-solid-svg-icons';
import "../module_mechanics/Styles/StockTransfer.css";

// ==============================================
// TOAST NOTIFICATION COMPONENT
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozst_toast-notification ozst_toast-${toast.type}`}>
            <div className="ozst_toast-content">
                <div className="ozst_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozst_toast-text">
                    <div className="ozst_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozst_toast-message">{toast.message}</div>
                </div>
                <button className="ozst_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// ORBIT ANIMATION COMPONENT - Clean Minimal Design
// ==============================================
const OrbitAnimation = ({ transferCount, totalShops }) => {
    const animationRef = useRef(null);

    useEffect(() => {
        const rings = document.querySelectorAll('.ozst-orbit-ring-modern');
        let angle = 0;
        
        const animate = () => {
            angle += 0.4;
            rings.forEach((ring, index) => {
                const speed = (index + 1) * 0.3;
                ring.style.transform = `rotate(${angle * speed * (index % 2 === 0 ? 1 : -1)}deg)`;
            });
            animationRef.current = requestAnimationFrame(animate);
        };
        
        animationRef.current = requestAnimationFrame(animate);
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);

    const transferPercentage = totalShops > 0 ? Math.min((transferCount / 10) * 100, 100) : 0;

    return (
        <div className="ozst-orbit-wrapper">
            <div className="ozst-orbit-container-modern">
                <div 
                    className="ozst-orbit-glow-modern"
                    style={{
                        opacity: 0.2 + (transferPercentage / 100) * 0.3,
                    }}
                />

                <div className="ozst-orbit-ring-modern ozst-orbit-ring-modern-1">
                    <div className="ozst-orbit-dot" style={{ backgroundColor: '#6366f1' }}>
                        <div className="ozst-orbit-dot-pulse" style={{ opacity: transferPercentage / 100 }} />
                    </div>
                </div>
                
                <div className="ozst-orbit-ring-modern ozst-orbit-ring-modern-2">
                    <div className="ozst-orbit-dot" style={{ backgroundColor: '#8b5cf6' }}>
                        <div className="ozst-orbit-dot-pulse" style={{ opacity: transferPercentage / 100 }} />
                    </div>
                </div>

                <div 
                    className="ozst-orbit-core-modern"
                    style={{
                        transform: `scale(${1 + (transferPercentage / 100) * 0.1})`,
                    }}
                >
                    <div className="ozst-core-content">
                        <span className="ozst-core-number">{transferCount}</span>
                        <span className="ozst-core-subtitle">Transfers</span>
                    </div>
                </div>

                {transferCount > 0 && (
                    <svg className="ozst-progress-ring" viewBox="0 0 100 100">
                        <circle
                            className="ozst-progress-ring-bg"
                            cx="50"
                            cy="50"
                            r="42"
                        />
                        <circle
                            className="ozst-progress-ring-fill"
                            cx="50"
                            cy="50"
                            r="42"
                            strokeDasharray={`${(transferPercentage / 100) * 264} 264`}
                            strokeLinecap="round"
                        />
                    </svg>
                )}
            </div>
        </div>
    );
};

// ==============================================
// MAIN COMPONENT
// ==============================================
const StockTransfer = () => {
    const { user, currentShop } = useAuth();

    // Refs
    const searchInputRef = useRef(null);
    const toastTimerRef = useRef(null);
    const isSubmittingRef = useRef(false);
    const draftsDropdownRef = useRef(null);

    // State
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [availableShops, setAvailableShops] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [targetShopId, setTargetShopId] = useState('');
    const [transferQuantity, setTransferQuantity] = useState(1);
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [isSaving, setIsSaving] = useState(false);
    const [transfers, setTransfers] = useState([]);
    const [showTransferHistory, setShowTransferHistory] = useState(false);
    const [transferFilter, setTransferFilter] = useState('all');
    const [shopSearchTerm, setShopSearchTerm] = useState('');
    const [showDraftsList, setShowDraftsList] = useState(false);
    const [savedDrafts, setSavedDrafts] = useState([]);
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [currentThemeColor, setCurrentThemeColor] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // ============================================
    // TRANSLATOR
    // ============================================
    const createTranslator = () => {
        let cache = new Map();

        const loadFromCache = () => {
            try {
                const cached = localStorage.getItem('ourzest_translations');
                if (cached) {
                    const parsed = JSON.parse(cached);
                    Object.entries(parsed).forEach(([key, value]) => {
                        cache.set(key, value);
                    });
                }
            } catch (e) {
                console.log("No cache found in Stock Transfer");
            }
        };

        const saveToCache = () => {
            try {
                const cacheObj = {};
                cache.forEach((value, key) => {
                    cacheObj[key] = value;
                });
                localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
            } catch (e) {
                console.error("Error saving to cache:", e);
            }
        };

        const translateAPI = async (text, targetLang) => {
            if (targetLang === 'en') return text;

            const dict = {
                'ur': {
                    'Stock Transfer': 'اسٹاک منتقلی',
                    'Transfer products from one shop to another': 'مصنوعات کو ایک شاپ سے دوسری شاپ میں منتقل کریں',
                    'Search products...': 'مصنوعات تلاش کریں...',
                    'Select Product': 'مصنوعات منتخب کریں',
                    'Transfer To': 'منتقل کریں',
                    'Select Shop': 'شاپ منتخب کریں',
                    'Quantity': 'مقدار',
                    'Notes': 'نوٹس',
                    'Transfer': 'منتقل کریں',
                    'Transferring...': 'منتقل ہو رہا ہے...',
                    'No products found': 'کوئی مصنوعات نہیں ملی',
                    'No shops available': 'کوئی شاپ دستیاب نہیں',
                    'Please select a product': 'براہ کرم ایک مصنوعات منتخب کریں',
                    'Please select a target shop': 'براہ کرم منزل شاپ منتخب کریں',
                    'Please enter quantity': 'براہ کرم مقدار درج کریں',
                    'Transfer successful!': 'منتقلگی کامیاب!',
                    'Transfer failed!': 'منتقلگی ناکام!',
                    'No product selected': 'کوئی مصنوعات منتخب نہیں',
                    'Available quantity': 'دستیاب مقدار',
                    'Transfer History': 'منتقلگی کی تاریخ',
                    'All': 'سب',
                    'Pending': 'زیر التواء',
                    'Approved': 'منظور شدہ',
                    'Completed': 'مکمل',
                    'Cancelled': 'منسوخ',
                    'Incoming': 'آنے والی',
                    'Outgoing': 'جانے والی',
                    'No transfers found': 'کوئی منتقلگی نہیں ملی',
                    'Transfer No': 'منتقلگی نمبر',
                    'From': 'سے',
                    'To': 'کو',
                    'Status': 'حالت',
                    'Date': 'تاریخ',
                    'Actions': 'اعمال',
                    'Approve': 'منظور کریں',
                    'Complete': 'مکمل کریں',
                    'Cancel': 'منسوخ کریں',
                    'Are you sure you want to cancel this transfer?': 'کیا آپ واقعی اس منتقلگی کو منسوخ کرنا چاہتے ہیں؟',
                    'Refresh': 'ریفریش کریں',
                    'Loading...': 'لوڈ ہو رہا ہے...',
                    'Product': 'مصنوعات',
                    'Shop': 'شاپ',
                    'Transfer Amount': 'منتقلگی کی مقدار',
                    'Success': 'کامیابی',
                    'Error': 'خرابی',
                    'Warning': 'انتباہ',
                    'Info': 'معلومات',
                    'Save Draft': 'ڈرافٹ محفوظ کریں',
                    'Drafts': 'ڈرافٹس',
                    'Saved Drafts': 'محفوظ کردہ ڈرافٹس',
                    'No drafts saved': 'کوئی ڈرافٹ محفوظ نہیں',
                    'Clear': 'صاف کریں',
                    'Clear Form': 'فارم صاف کریں',
                    'Draft saved': 'ڈرافٹ محفوظ ہو گیا',
                    'Draft loaded': 'ڈرافٹ لوڈ ہو گیا',
                    'Draft cleared': 'ڈرافٹ صاف ہو گیا',
                    'Current': 'موجودہ',
                    'Transfer Settings': 'منتقلگی کی ترتیبات',
                    'Search shops...': 'شاپس تلاش کریں...',
                    'No shops found': 'کوئی شاپ نہیں ملی',
                    'of': 'میں سے',
                    'shop(s)': 'شاپ(یں)',
                    'selected': 'منتخب کردہ',
                    'Transfer saved successfully!': 'منتقلگی کامیابی سے محفوظ ہو گئی!',
                    'Failed to save transfer': 'منتقلگی محفوظ کرنے میں ناکامی',
                    'Transfer cancelled successfully': 'منتقلگی کامیابی سے منسوخ ہو گئی',
                    'Transfer approved successfully': 'منتقلگی کامیابی سے منظور ہو گئی',
                    'Transfer completed successfully': 'منتقلگی کامیابی سے مکمل ہو گئی'
                },
                'ar': {
                    'Stock Transfer': 'نقل المخزون',
                    'Transfer products from one shop to another': 'نقل المنتجات من متجر إلى آخر',
                    'Search products...': 'ابحث عن المنتجات...',
                    'Select Product': 'اختر المنتج',
                    'Transfer To': 'نقل إلى',
                    'Select Shop': 'اختر المتجر',
                    'Quantity': 'الكمية',
                    'Notes': 'ملاحظات',
                    'Transfer': 'نقل',
                    'Transferring...': 'جاري النقل...',
                    'No products found': 'لم يتم العثور على منتجات',
                    'No shops available': 'لا توجد متاجر متاحة',
                    'Please select a product': 'يرجى اختيار منتج',
                    'Please select a target shop': 'يرجى اختيار المتجر الهدف',
                    'Please enter quantity': 'يرجى إدخال الكمية',
                    'Transfer successful!': 'تم النقل بنجاح!',
                    'Transfer failed!': 'فشل النقل!',
                    'No product selected': 'لم يتم اختيار منتج',
                    'Available quantity': 'الكمية المتاحة',
                    'Transfer History': 'سجل النقل',
                    'All': 'الكل',
                    'Pending': 'قيد الانتظار',
                    'Approved': 'موافق عليه',
                    'Completed': 'مكتمل',
                    'Cancelled': 'ملغي',
                    'Incoming': 'وارد',
                    'Outgoing': 'صادر',
                    'No transfers found': 'لم يتم العثور على عمليات نقل',
                    'Transfer No': 'رقم النقل',
                    'From': 'من',
                    'To': 'إلى',
                    'Status': 'الحالة',
                    'Date': 'التاريخ',
                    'Actions': 'الإجراءات',
                    'Approve': 'موافقة',
                    'Complete': 'إكمال',
                    'Cancel': 'إلغاء',
                    'Are you sure you want to cancel this transfer?': 'هل أنت متأكد من رغبتك في إلغاء هذا النقل؟',
                    'Refresh': 'تحديث',
                    'Loading...': 'جاري التحميل...',
                    'Product': 'المنتج',
                    'Shop': 'المتجر',
                    'Transfer Amount': 'كمية النقل',
                    'Success': 'نجاح',
                    'Error': 'خطأ',
                    'Warning': 'تحذير',
                    'Info': 'معلومات',
                    'Save Draft': 'حفظ المسودة',
                    'Drafts': 'المسودات',
                    'Saved Drafts': 'المسودات المحفوظة',
                    'No drafts saved': 'لا توجد مسودات محفوظة',
                    'Clear': 'مسح',
                    'Clear Form': 'مسح النموذج',
                    'Draft saved': 'تم حفظ المسودة',
                    'Draft loaded': 'تم تحميل المسودة',
                    'Draft cleared': 'تم مسح المسودة',
                    'Current': 'حالي',
                    'Transfer Settings': 'إعدادات النقل',
                    'Search shops...': 'ابحث عن المتاجر...',
                    'No shops found': 'لم يتم العثور على متاجر',
                    'of': 'من',
                    'shop(s)': 'متجر(متاجر)',
                    'selected': 'المحدد',
                    'Transfer saved successfully!': 'تم حفظ النقل بنجاح!',
                    'Failed to save transfer': 'فشل في حفظ النقل',
                    'Transfer cancelled successfully': 'تم إلغاء النقل بنجاح',
                    'Transfer approved successfully': 'تم الموافقة على النقل بنجاح',
                    'Transfer completed successfully': 'تم إكمال النقل بنجاح'
                }
            };

            if (dict[targetLang] && dict[targetLang][text]) {
                return dict[targetLang][text];
            }

            try {
                const response = await fetch('https://translate.argosopentech.com/translate', {
                    method: 'POST',
                    body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                    headers: { 'Content-Type': 'application/json' }
                });

                if (response.ok) {
                    const data = await response.json();
                    return data.translatedText;
                }
            } catch (e) {
                console.log("LibreTranslate failed, trying MyMemory");
            }

            try {
                const response = await fetch(
                    `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
                );
                if (response.ok) {
                    const data = await response.json();
                    return data.responseData.translatedText;
                }
            } catch (e) {
                console.log("MyMemory translation failed");
            }

            return text;
        };

        const translateText = async (text, targetLang) => {
            const cacheKey = `${text}_${targetLang}`;

            if (cache.has(cacheKey)) {
                return cache.get(cacheKey);
            }

            const translated = await translateAPI(text, targetLang);
            cache.set(cacheKey, translated);
            saveToCache();

            return translated;
        };

        const defaultTexts = {
            stockTransfer: 'Stock Transfer',
            transferProducts: 'Transfer products from one shop to another',
            searchProducts: 'Search products...',
            selectProduct: 'Select Product',
            transferTo: 'Transfer To',
            selectShop: 'Select Shop',
            quantity: 'Quantity',
            notes: 'Notes',
            transfer: 'Transfer',
            transferring: 'Transferring...',
            noProducts: 'No products found',
            noShops: 'No shops available',
            selectProductWarning: 'Please select a product',
            selectShopWarning: 'Please select a target shop',
            enterQuantity: 'Please enter quantity',
            transferSuccess: 'Transfer successful!',
            transferFailed: 'Transfer failed!',
            noProductSelected: 'No product selected',
            availableQuantity: 'Available quantity',
            transferHistory: 'Transfer History',
            all: 'All',
            pending: 'Pending',
            approved: 'Approved',
            completed: 'Completed',
            cancelled: 'Cancelled',
            incoming: 'Incoming',
            outgoing: 'Outgoing',
            noTransfers: 'No transfers found',
            transferNo: 'Transfer No',
            from: 'From',
            to: 'To',
            status: 'Status',
            date: 'Date',
            actions: 'Actions',
            approve: 'Approve',
            complete: 'Complete',
            cancel: 'Cancel',
            cancelConfirm: 'Are you sure you want to cancel this transfer?',
            refresh: 'Refresh',
            loading: 'Loading...',
            product: 'Product',
            shop: 'Shop',
            transferAmount: 'Transfer Amount',
            success: 'Success',
            error: 'Error',
            warning: 'Warning',
            info: 'Info',
            saveDraft: 'Save Draft',
            drafts: 'Drafts',
            savedDrafts: 'Saved Drafts',
            noDrafts: 'No drafts saved',
            clear: 'Clear',
            clearForm: 'Clear Form',
            draftSaved: 'Draft saved',
            draftLoaded: 'Draft loaded',
            draftCleared: 'Draft cleared',
            current: 'Current',
            transferSettings: 'Transfer Settings',
            searchShops: 'Search shops...',
            noShopsFound: 'No shops found',
            of: 'of',
            shopCount: 'shop(s)',
            selected: 'selected',
            transferSaved: 'Transfer saved successfully!',
            transferSaveFailed: 'Failed to save transfer',
            transferCancelled: 'Transfer cancelled successfully',
            transferApproved: 'Transfer approved successfully',
            transferCompleted: 'Transfer completed successfully'
        };

        const getTranslations = async (lang) => {
            if (lang === 'en') {
                return defaultTexts;
            }

            const translations = {};
            const promises = [];

            Object.keys(defaultTexts).forEach(key => {
                const cacheKey = `${defaultTexts[key]}_${lang}`;
                if (cache.has(cacheKey)) {
                    translations[key] = cache.get(cacheKey);
                } else {
                    promises.push(
                        translateText(defaultTexts[key], lang).then(translated => {
                            translations[key] = translated;
                        })
                    );
                }
            });

            if (promises.length > 0) {
                await Promise.all(promises);
                saveToCache();
            }

            return translations;
        };

        loadFromCache();

        return {
            getTranslations,
            translateText
        };
    };

    const translator = createTranslator();

    // ============================================
    // THEME SYNC
    // ============================================
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        document.documentElement.style.setProperty('--ozst-primary', color);
        document.documentElement.style.setProperty('--ozst-primary-dark', color);
        document.documentElement.style.setProperty('--ozst-primary-light', color);
        
        let r, g, b;
        if (color.startsWith('#')) {
            r = parseInt(color.slice(1, 3), 16);
            g = parseInt(color.slice(3, 5), 16);
            b = parseInt(color.slice(5, 7), 16);
        } else {
            r = 99; g = 102; b = 241;
        }
        
        let style = document.getElementById('ozst-dynamic-orb-styles');
        if (!style) {
            style = document.createElement('style');
            style.id = 'ozst-dynamic-orb-styles';
            document.head.appendChild(style);
        }
        
        style.textContent = `
            .ozst-orbit-wrapper .ozst-orbit-glow-modern {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.05) 70%) !important;
            }
            .ozst-orbit-ring-modern {
                border-color: rgba(${r}, ${g}, ${b}, 0.15) !important;
            }
            .ozst-orbit-dot {
                background-color: rgba(${r}, ${g}, ${b}, 1) !important;
            }
            .ozst-progress-ring-fill {
                stroke: rgba(${r}, ${g}, ${b}, 0.4) !important;
            }
        `;
        setCurrentThemeColor(color);
    }, []);

    // ============================================
    // DARK MODE SYNC
    // ============================================
    useEffect(() => {
        const syncDarkMode = () => {
            const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDarkMode);
            
            const container = document.querySelector('.ozst-container');
            if (container) {
                if (isDarkMode) {
                    container.classList.add('ozst-dark');
                } else {
                    container.classList.remove('ozst-dark');
                }
            }
            
            const header = document.querySelector('.ozst-sticky-header');
            if (header) {
                if (isDarkMode) {
                    header.classList.add('ozst-header-dark');
                } else {
                    header.classList.remove('ozst-header-dark');
                }
            }
        };
        
        syncDarkMode();
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };
        
        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
                
                const container = document.querySelector('.ozst-container');
                if (container) {
                    if (isDark) {
                        container.classList.add('ozst-dark');
                    } else {
                        container.classList.remove('ozst-dark');
                    }
                }
                
                const header = document.querySelector('.ozst-sticky-header');
                if (header) {
                    if (isDark) {
                        header.classList.add('ozst-header-dark');
                    } else {
                        header.classList.remove('ozst-header-dark');
                    }
                }
            } else {
                syncDarkMode();
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);
        
        const interval = setInterval(() => {
            const storedDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            if (storedDarkMode !== darkMode) {
                syncDarkMode();
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
            clearInterval(interval);
        };
    }, [darkMode]);

    // ============================================
    // LANGUAGE SYNC
    // ============================================
    useEffect(() => {
        const loadTranslations = async (lang) => {
            const trans = await translator.getTranslations(lang);
            setTranslations(trans);
        };
        
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };
        
        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };
        
        loadTranslations(currentLanguage);
        
        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);
        
        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // ============================================
    // RTL SYNC
    // ============================================
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozst-container');
        
        if (container) {
            if (isRTL) {
                container.classList.add('ozst-rtl');
                container.classList.remove('ozst-ltr');
            } else {
                container.classList.add('ozst-ltr');
                container.classList.remove('ozst-rtl');
            }
        }
        
        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    // ============================================
    // THEME INIT
    // ============================================
    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                const newTheme = e.newValue || '#6366f1';
                applyTheme(newTheme);
            }
        };
        
        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);
        
        const interval = setInterval(() => {
            const storedTheme = localStorage.getItem('ourzest_theme');
            if (storedTheme && storedTheme !== currentThemeColor) {
                applyTheme(storedTheme);
            }
        }, 500);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
            clearInterval(interval);
        };
    }, [applyTheme, currentThemeColor]);

    // ============================================
    // TOAST
    // ============================================
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            isSubmittingRef.current = false;
        };
    }, []);

    // ============================================
    // DRAFTS
    // ============================================
    const loadDrafts = () => {
        const drafts = JSON.parse(localStorage.getItem('stock_transfer_drafts') || '[]');
        const filteredDrafts = currentShop ? drafts.filter(d => d.shop_id === currentShop?.shop_id) : drafts;
        setSavedDrafts(filteredDrafts);
    };

    useEffect(() => {
        loadDrafts();
    }, [currentShop]);

    const saveDraft = () => {
        if (!selectedProduct && !targetShopId && transferQuantity === 1 && !notes) {
            showToast('warning', 'Cannot save empty draft');
            return;
        }
        
        const draftId = Date.now();
        const draft = {
            id: draftId,
            selectedProductId: selectedProduct?.id || null,
            selectedProductName: selectedProduct?.product_name || '',
            targetShopId: targetShopId || '',
            transferQuantity: transferQuantity || 1,
            notes: notes || '',
            savedAt: new Date().toISOString(),
            shop_id: currentShop?.shop_id,
            productCode: selectedProduct?.product_code || ''
        };
        
        const existingDrafts = JSON.parse(localStorage.getItem('stock_transfer_drafts') || '[]');
        existingDrafts.unshift(draft);
        const limitedDrafts = existingDrafts.slice(0, 20);
        localStorage.setItem('stock_transfer_drafts', JSON.stringify(limitedDrafts));
        
        showToast('success', translations.draftSaved || 'Draft saved successfully!');
        loadDrafts();
    };

    const loadDraft = (draft) => {
        if (draft.selectedProductId) {
            const product = products.find(p => p.id === draft.selectedProductId);
            if (product) {
                setSelectedProduct(product);
            }
        }
        setTargetShopId(draft.targetShopId || '');
        setTransferQuantity(draft.transferQuantity || 1);
        setNotes(draft.notes || '');
        setShowDraftsList(false);
        showToast('success', translations.draftLoaded || 'Draft loaded successfully!');
    };

    const deleteDraft = (draftId) => {
        const drafts = JSON.parse(localStorage.getItem('stock_transfer_drafts') || '[]');
        const filteredDrafts = drafts.filter(d => d.id !== draftId);
        localStorage.setItem('stock_transfer_drafts', JSON.stringify(filteredDrafts));
        loadDrafts();
        showToast('success', 'Draft deleted successfully!');
    };

    const clearForm = () => {
        setSelectedProduct(null);
        setTargetShopId('');
        setTransferQuantity(1);
        setNotes('');
        setSearchTerm('');
        setShopSearchTerm('');
        showToast('success', translations.draftCleared || 'Form cleared');
    };

    // ============================================
    // DATA FETCHING
    // ============================================
    const fetchUserShops = useCallback(async () => {
        if (!user?.id) {
            return [];
        }

        try {
            const params = new URLSearchParams({
                action: 'get_all_shops',
                current_user_id: String(user.id),
                current_user_role: user.role || '',
                centralized_only: '1'
            });

            const response = await fetch(`https://ourzestapp.online/oz_backend/root_users.php?${params}&t=${Date.now()}`);
            const data = await response.json();

            if (data.success && data.data && data.data.length > 0) {
                const shops = data.data.filter(shop => shop.shop_id !== currentShop?.shop_id);
                setAvailableShops(shops);
                return shops;
            }

            setAvailableShops([]);
            showToast('info', translations.noShops || 'No shops available for transfer');
            return [];

        } catch (error) {
            console.error('Error fetching shops:', error);
            setAvailableShops([]);
            showToast('error', 'Failed to load shops: ' + error.message);
            return [];
        }
    }, [user?.id, user?.role, currentShop?.shop_id, translations, showToast]);

    const fetchProducts = useCallback(async (search = '') => {
        if (!currentShop) {
            showToast('error', translations.noProductSelected || 'Please select a shop first');
            return;
        }

        if (!user?.username) {
            showToast('error', 'User not authenticated');
            return;
        }

        setLoading(true);
        try {
            const requestData = {
                action: 'get_records',
                username: user.username,
                shop_id: currentShop.shop_id
            };
            
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(requestData)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            if (data.status === 'success') {
                let productList = data.data || [];
                
                if (search.trim() !== '') {
                    const lowerTerm = search.toLowerCase();
                    productList = productList.filter(p =>
                        p.product_name?.toLowerCase().includes(lowerTerm) ||
                        p.batch_no?.toLowerCase().includes(lowerTerm) ||
                        p.barcode?.toLowerCase().includes(lowerTerm) ||
                        p.product_code?.toLowerCase().includes(lowerTerm)
                    );
                }
                
                setProducts(productList);
                setFilteredProducts(productList);
            } else {
                showToast('error', data.message || 'Failed to load products');
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            showToast('error', `Network error: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [currentShop, user?.username, translations, showToast]);

    const fetchTransfers = useCallback(async () => {
        if (!user?.username) return;

        try {
            const requestData = {
                action: 'get_transfers',
                username: user.username,
                shop_id: currentShop?.shop_id,
                status: transferFilter === 'all' ? null : transferFilter
            };
            
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                setTransfers(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching transfers:', error);
        }
    }, [user?.username, currentShop?.shop_id, transferFilter]);

    // ============================================
    // LOAD DATA
    // ============================================
    useEffect(() => {
        const loadData = async () => {
            await fetchUserShops();
            if (currentShop) {
                fetchProducts();
                fetchTransfers();
            }
        };
        loadData();
    }, [currentShop, fetchUserShops, fetchProducts, fetchTransfers]);

    // ============================================
    // HANDLERS
    // ============================================
    const handleSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        fetchProducts(term);
    };

    const handleProductSelect = (product) => {
        setSelectedProduct(product);
        setTransferQuantity(1);
    };

    const handleTransfer = async () => {
        if (!selectedProduct) {
            showToast('warning', translations.selectProductWarning || 'Please select a product');
            return;
        }

        if (!targetShopId) {
            showToast('warning', translations.selectShopWarning || 'Please select a target shop');
            return;
        }

        if (transferQuantity <= 0 || transferQuantity > selectedProduct.quantity) {
            showToast('warning', `Please enter a valid quantity (max ${selectedProduct.quantity})`);
            return;
        }

        setIsSaving(true);
        isSubmittingRef.current = true;

        try {
            const requestData = {
                action: 'transfer_product',
                product_id: selectedProduct.id,
                source_shop_id: currentShop.shop_id,
                target_shop_id: parseInt(targetShopId),
                quantity: transferQuantity,
                username: user?.username,
                notes: notes
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', translations.transferSuccess || 'Transfer successful!');
                setSelectedProduct(null);
                setTargetShopId('');
                setTransferQuantity(1);
                setNotes('');
                fetchProducts(searchTerm);
                fetchTransfers();
            } else {
                showToast('error', data.message || translations.transferFailed || 'Transfer failed!');
            }
        } catch (error) {
            console.error('Error transferring product:', error);
            showToast('error', translations.transferFailed || 'Transfer failed!');
        } finally {
            setIsSaving(false);
            isSubmittingRef.current = false;
        }
    };

    const handleCancelTransfer = async (transferId) => {
        if (!window.confirm(translations.cancelConfirm || 'Are you sure you want to cancel this transfer?')) {
            return;
        }

        try {
            const requestData = {
                action: 'cancel_transfer',
                transfer_id: transferId,
                username: user?.username
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', translations.transferCancelled || 'Transfer cancelled successfully');
                fetchTransfers();
                fetchProducts(searchTerm);
            } else {
                showToast('error', data.message || 'Failed to cancel transfer');
            }
        } catch (error) {
            console.error('Error cancelling transfer:', error);
            showToast('error', 'Failed to cancel transfer');
        }
    };

    const handleApproveTransfer = async (transferId) => {
        try {
            const requestData = {
                action: 'approve_transfer',
                transfer_id: transferId,
                username: user?.username
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', translations.transferApproved || 'Transfer approved successfully');
                fetchTransfers();
            } else {
                showToast('error', data.message || 'Failed to approve transfer');
            }
        } catch (error) {
            console.error('Error approving transfer:', error);
            showToast('error', 'Failed to approve transfer');
        }
    };

    const handleCompleteTransfer = async (transferId) => {
        try {
            const requestData = {
                action: 'complete_transfer',
                transfer_id: transferId,
                username: user?.username
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', translations.transferCompleted || 'Transfer completed successfully');
                fetchTransfers();
                fetchProducts(searchTerm);
            } else {
                showToast('error', data.message || 'Failed to complete transfer');
            }
        } catch (error) {
            console.error('Error completing transfer:', error);
            showToast('error', 'Failed to complete transfer');
        }
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'pending': { class: 'status-pending', icon: faClock, label: translations.pending || 'Pending' },
            'approved': { class: 'status-approved', icon: faCheck, label: translations.approved || 'Approved' },
            'completed': { class: 'status-completed', icon: faCheckCircle, label: translations.completed || 'Completed' },
            'cancelled': { class: 'status-cancelled', icon: faBan, label: translations.cancelled || 'Cancelled' }
        };
        return statusMap[status] || statusMap['pending'];
    };

    const filteredShops = availableShops.filter(shop =>
        shop.shop_name?.toLowerCase().includes(shopSearchTerm.toLowerCase()) ||
        (shop.shop_code && shop.shop_code.toLowerCase().includes(shopSearchTerm.toLowerCase()))
    );

    const totalTransfers = transfers.length;
    const pendingTransfers = transfers.filter(t => t.status === 'pending').length;

    // ============================================
    // RENDER
    // ============================================
    return (
        <div className={`ozst-container ${isRTL ? 'ozst-rtl' : 'ozst-ltr'} ${darkMode ? 'ozst-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            {/* Modern Animated Sticky Header */}
            <div className="ozst-sticky-header">
                <div className="ozst-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozst-header-content">
                    <div className="ozst-header-left">
                        <div className="ozst-header-title">
                            <h1>
                               
                                {translations.stockTransfer || 'Stock Transfer'}
                            </h1>
                            <p>{translations.transferProducts || 'Transfer products from one shop to another'}</p>
                        </div>
                    </div>
                    
                    <div className="ozst-header-right">
                        <div className="ozst-header-actions">
                            <button 
                                className="ozst-action-btn clear-draft" 
                                onClick={clearForm}
                                disabled={loading || isSaving}
                                title={translations.clearForm || 'Clear Form'}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                                <span>{translations.clear || 'Clear'}</span>
                            </button>
                            
                            <button 
                                className="ozst-action-btn save-draft" 
                                onClick={saveDraft}
                                disabled={loading || isSaving}
                                title={translations.saveDraft || 'Save as Draft'}
                            >
                                <FontAwesomeIcon icon={faCopy} />
                                <span>{translations.saveDraft || 'Save Draft'}</span>
                            </button>
                            
                            <div className="ozst-drafts-dropdown-container" ref={draftsDropdownRef}>
                                <button 
                                    className="ozst-action-btn drafts-btn" 
                                    onClick={() => setShowDraftsList(!showDraftsList)}
                                    title={translations.drafts || 'View Drafts'}
                                >
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    <span>{translations.drafts || 'Drafts'}</span>
                                    {savedDrafts.length > 0 && <span className="drafts-badge">{savedDrafts.length}</span>}
                                </button>
                                
                                {showDraftsList && (
                                    <div className="ozst-drafts-dropdown">
                                        <div className="drafts-header">
                                            <h4>{translations.savedDrafts || 'Saved Drafts'}</h4>
                                            <button onClick={() => setShowDraftsList(false)}>×</button>
                                        </div>
                                        <div className="drafts-list">
                                            {savedDrafts.length === 0 ? (
                                                <div className="no-drafts">{translations.noDrafts || 'No drafts saved'}</div>
                                            ) : (
                                                savedDrafts.map(draft => (
                                                    <div key={draft.id} className="draft-item">
                                                        <div className="draft-info" onClick={() => loadDraft(draft)}>
                                                            <div className="draft-name">
                                                                {draft.selectedProductName || 'No product'} → {draft.transferQuantity} qty
                                                            </div>
                                                            <div className="draft-date">
                                                                {new Date(draft.savedAt).toLocaleDateString()}
                                                            </div>
                                                        </div>
                                                        <button 
                                                            className="delete-draft-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                deleteDraft(draft.id);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            <button
                                className={`ozst-action-btn history-btn ${showTransferHistory ? 'active' : ''}`}
                                onClick={() => setShowTransferHistory(!showTransferHistory)}
                                title={translations.transferHistory || 'Transfer History'}
                            >
                                <FontAwesomeIcon icon={faHistory} />
                                <span>{translations.transferHistory || 'History'}</span>
                                {pendingTransfers > 0 && <span className="drafts-badge">{pendingTransfers}</span>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content - Two Column Layout */}
            <div className="ozst-main">
                {/* Left Panel - Transfer Form */}
                <div className="ozst-form-panel">
                    <div className="ozst-panel-header">
                        <h3>
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            {translations.transferSettings || 'Transfer Settings'}
                        </h3>
                    </div>

                    {/* Source Shop Info */}
                    <div className="ozst-source-info">
                        <div className="ozst-shop-badge">
                            <FontAwesomeIcon icon={faStore} />
                            <span>{translations.from || 'From'}:</span>
                            <strong>{currentShop?.shop_name || 'Not selected'}</strong>
                        </div>
                    </div>

                    {/* Product Selection */}
                    <div className="ozst-form-group">
                        <label>{translations.selectProduct || 'Select Product'}</label>
                        <div className="ozst-search-wrapper">
                            <FontAwesomeIcon icon={faSearch} className="ozst-search-icon" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder={translations.searchProducts || 'Search products...'}
                                value={searchTerm}
                                onChange={handleSearch}
                                className="ozst-search-input"
                            />
                        </div>
                        
                        <div className="ozst-products-list">
                            {loading ? (
                                <div className="ozst-loading-state">
                                    <div className="ozst-spinner"></div>
                                    <span>{translations.loading || 'Loading...'}</span>
                                </div>
                            ) : filteredProducts.length === 0 ? (
                                <div className="ozst-empty-state">
                                    <FontAwesomeIcon icon={faBox} />
                                    <p>{translations.noProducts || 'No products found'}</p>
                                    {searchTerm && <small>{translations.adjustSearch || 'Try adjusting your search'}</small>}
                                </div>
                            ) : (
                                filteredProducts.map(product => (
                                    <div
                                        key={product.id}
                                        className={`ozst-product-item ${selectedProduct?.id === product.id ? 'selected' : ''}`}
                                        onClick={() => handleProductSelect(product)}
                                    >
                                        <div className="ozst-product-check">
                                            <FontAwesomeIcon icon={selectedProduct?.id === product.id ? faCheckCircle : faCircle} />
                                        </div>
                                        <div className="ozst-product-info">
                                            <div className="ozst-product-name">
                                                {product.product_name}
                                                {product.product_code && (
                                                    <span className="ozst-product-code">{product.product_code}</span>
                                                )}
                                            </div>
                                            <div className="ozst-product-meta">
                                                {product.batch_no && (
                                                    <span className="ozst-meta-tag">
                                                        <FontAwesomeIcon icon={faTag} />
                                                        {product.batch_no}
                                                    </span>
                                                )}
                                                {product.barcode && (
                                                    <span className="ozst-meta-tag">
                                                        <FontAwesomeIcon icon={faBarcode} />
                                                        {product.barcode}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="ozst-product-quantity">
                                            {translations.availableQuantity || 'Available'}: {product.quantity}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Selected Product Info */}
                    {selectedProduct && (
                        <div className="ozst-selected-product">
                            <div className="ozst-selected-info">
                                <span className="ozst-selected-name">
                                    <FontAwesomeIcon icon={faCheck} />
                                    {selectedProduct.product_name}
                                </span>
                                <span className="ozst-selected-qty">
                                    {translations.availableQuantity || 'Available'}: {selectedProduct.quantity}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Target Shop */}
                    <div className="ozst-form-group">
                        <label>{translations.transferTo || 'Transfer To'}</label>
                        <select
                            className="ozst-select"
                            value={targetShopId}
                            onChange={(e) => setTargetShopId(e.target.value)}
                        >
                            <option value="">{translations.selectShop || 'Select Shop'}</option>
                            {availableShops.map(shop => (
                                <option key={shop.shop_id} value={shop.shop_id}>
                                    {shop.shop_name} {shop.shop_code ? `(${shop.shop_code})` : ''}
                                </option>
                            ))}
                        </select>
                        {availableShops.length === 0 && (
                            <div className="ozst-form-hint">
                                <FontAwesomeIcon icon={faInfoCircle} />
                                {translations.noShops || 'No shops available for transfer'}
                            </div>
                        )}
                    </div>

                    {/* Quantity */}
                    <div className="ozst-form-group">
                        <label>{translations.quantity || 'Quantity'}</label>
                        <div className="ozst-quantity-control">
                            <button
                                className="ozst-qty-btn"
                                onClick={() => setTransferQuantity(Math.max(1, transferQuantity - 1))}
                                disabled={!selectedProduct}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                            <input
                                type="number"
                                className="ozst-qty-input"
                                value={transferQuantity}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value) || 1;
                                    setTransferQuantity(Math.max(1, Math.min(val, selectedProduct?.quantity || 1)));
                                }}
                                min="1"
                                max={selectedProduct?.quantity || 1}
                                disabled={!selectedProduct}
                            />
                            <button
                                className="ozst-qty-btn"
                                onClick={() => setTransferQuantity(Math.min(selectedProduct?.quantity || 1, transferQuantity + 1))}
                                disabled={!selectedProduct}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                            </button>
                            {selectedProduct && (
                                <span className="ozst-qty-max">
                                    max: {selectedProduct.quantity}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="ozst-form-group">
                        <label>{translations.notes || 'Notes'}</label>
                        <textarea
                            className="ozst-textarea"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Optional notes about this transfer"
                            rows="2"
                        />
                    </div>

                    {/* Transfer Button */}
                    <button
                        className={`ozst-transfer-btn ${(!selectedProduct || !targetShopId) ? 'disabled' : ''}`}
                        onClick={handleTransfer}
                        disabled={!selectedProduct || !targetShopId || isSaving}
                    >
                        {isSaving ? (
                            <>
                                <div className="ozst-spinner-small"></div>
                                {translations.transferring || 'Transferring...'}
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faExchangeAlt} />
                                {translations.transfer || 'Transfer'}
                            </>
                        )}
                    </button>
                </div>

                {/* Right Panel - Transfer History with Animation */}
                <div className="ozst-history-panel">
                    {/* Animation Section - Top */}
                    <div className="ozst-orbit-wrapper">
                        <OrbitAnimation 
                            transferCount={totalTransfers}
                            totalShops={availableShops.length || 1}
                        />
                    </div>

                    {/* History Section - Bottom */}
                    <div className="ozst-history-section">
                        <div className="ozst-panel-header">
                            <h3>
                                <FontAwesomeIcon icon={faHistory} />
                                {translations.transferHistory || 'Transfer History'}
                                <span className="ozst-history-count">
                                    {totalTransfers} {translations.transfers || 'transfers'}
                                </span>
                            </h3>
                            <button
                                className="ozst-refresh-btn-small"
                                onClick={() => {
                                    fetchTransfers();
                                    fetchProducts(searchTerm);
                                }}
                                disabled={loading}
                            >
                                <FontAwesomeIcon icon={faSync} spin={loading} />
                            </button>
                        </div>

                        {/* Filter Tabs */}
                        <div className="ozst-history-filters">
                            {['all', 'pending', 'approved', 'completed', 'cancelled'].map(status => (
                                <button
                                    key={status}
                                    className={`ozst-filter-tab ${transferFilter === status ? 'active' : ''}`}
                                    onClick={() => setTransferFilter(status)}
                                >
                                    {status === 'all' ? (translations.all || 'All') :
                                     status === 'pending' ? (translations.pending || 'Pending') :
                                     status === 'approved' ? (translations.approved || 'Approved') :
                                     status === 'completed' ? (translations.completed || 'Completed') :
                                     (translations.cancelled || 'Cancelled')}
                                </button>
                            ))}
                        </div>

                        <div className="ozst-history-list">
                            {transfers.length === 0 ? (
                                <div className="ozst-empty-state">
                                    <FontAwesomeIcon icon={faClipboardList} />
                                    <p>{translations.noTransfers || 'No transfers found'}</p>
                                </div>
                            ) : (
                                transfers.map(transfer => {
                                    const statusBadge = getStatusBadge(transfer.status);
                                    const isIncoming = transfer.target_shop_id === currentShop?.shop_id;
                                    const isOutgoing = transfer.source_shop_id === currentShop?.shop_id;
                                    
                                    return (
                                        <div key={transfer.id} className="ozst-history-item">
                                            <div className="ozst-history-header">
                                                <span className="ozst-history-no">
                                                    <FontAwesomeIcon icon={faFileAlt} />
                                                    {transfer.transfer_no}
                                                </span>
                                                <span className={`ozst-history-status ${statusBadge.class}`}>
                                                    <FontAwesomeIcon icon={statusBadge.icon} />
                                                    {statusBadge.label}
                                                </span>
                                            </div>
                                            <div className="ozst-history-details">
                                                <div className="ozst-history-product">
                                                    <FontAwesomeIcon icon={faBox} />
                                                    {transfer.product_name}
                                                </div>
                                                <div className="ozst-history-direction">
                                                    {isIncoming ? (
                                                        <span className="direction-incoming">
                                                            <FontAwesomeIcon icon={faArrowLeft} />
                                                            {translations.incoming || 'Incoming'}
                                                        </span>
                                                    ) : isOutgoing ? (
                                                        <span className="direction-outgoing">
                                                            <FontAwesomeIcon icon={faArrowRight} />
                                                            {translations.outgoing || 'Outgoing'}
                                                        </span>
                                                    ) : (
                                                        <span className="direction-other">
                                                            <FontAwesomeIcon icon={faExchangeAlt} />
                                                            {translations.transfer || 'Transfer'}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="ozst-history-shops">
                                                    <span className="ozst-shop-from">
                                                        {transfer.source_shop_name}
                                                    </span>
                                                    <FontAwesomeIcon icon={faArrowRight} className="ozst-arrow-icon" />
                                                    <span className="ozst-shop-to">
                                                        {transfer.target_shop_name}
                                                    </span>
                                                </div>
                                                <div className="ozst-history-meta">
                                                    <span className="ozst-history-qty">
                                                        {translations.quantity || 'Qty'}: {transfer.quantity}
                                                    </span>
                                                    <span className="ozst-history-date">
                                                        {new Date(transfer.transfer_date).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                {transfer.notes && (
                                                    <div className="ozst-history-notes">
                                                        <FontAwesomeIcon icon={faInfoCircle} />
                                                        {transfer.notes}
                                                    </div>
                                                )}
                                                {transfer.status === 'pending' && isOutgoing && (
                                                    <div className="ozst-history-actions">
                                                        <button
                                                            className="ozst-action-approve"
                                                            onClick={() => handleApproveTransfer(transfer.id)}
                                                        >
                                                            <FontAwesomeIcon icon={faCheck} />
                                                            {translations.approve || 'Approve'}
                                                        </button>
                                                        <button
                                                            className="ozst-action-cancel"
                                                            onClick={() => handleCancelTransfer(transfer.id)}
                                                        >
                                                            <FontAwesomeIcon icon={faBan} />
                                                            {translations.cancel || 'Cancel'}
                                                        </button>
                                                    </div>
                                                )}
                                                {transfer.status === 'approved' && isIncoming && (
                                                    <div className="ozst-history-actions">
                                                        <button
                                                            className="ozst-action-complete"
                                                            onClick={() => handleCompleteTransfer(transfer.id)}
                                                        >
                                                            <FontAwesomeIcon icon={faCheckCircle} />
                                                            {translations.complete || 'Complete'}
                                                        </button>
                                                        <button
                                                            className="ozst-action-cancel"
                                                            onClick={() => handleCancelTransfer(transfer.id)}
                                                        >
                                                            <FontAwesomeIcon icon={faBan} />
                                                            {translations.cancel || 'Cancel'}
                                                        </button>
                                                    </div>
                                                )}
                                                {transfer.status === 'pending' && isIncoming && (
                                                    <div className="ozst-history-actions">
                                                        <button
                                                            className="ozst-action-cancel"
                                                            onClick={() => handleCancelTransfer(transfer.id)}
                                                        >
                                                            <FontAwesomeIcon icon={faBan} />
                                                            {translations.cancel || 'Cancel'}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StockTransfer;